import styled, { css, keyframes } from 'styled-components';
import { SaveButtonStatus } from '../../../hooks/useSaveButtonStatus';
import theme from '../../../theme';

const fadeIn = keyframes`
    from { opacity: 0; transform: scale(0.7); }
    to   { opacity: 1; transform: scale(1); }
`;

interface StyledSaveButtonProps {
    status: SaveButtonStatus;
    disabled?: boolean;
}

export const StyledSaveButton = styled.button<StyledSaveButtonProps>`
    border: 1px solid ${theme.colors.border.odfjellSection};
    background: ${theme.colors.background.white};
    color: ${theme.colors.text.odfjellSection};
    vertical-align: middle;
    outline: 0;
    font-size: 0.7rem;
    font-weight: 500;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 3px;
    padding: 2px 6px;
    min-width: 64px;
    transition: background 200ms ease, border-color 200ms ease;

    &:focus-visible {
        outline: 2px solid ${theme.colors.text.odfjellSection};
        outline-offset: 2px;
    }

    /* Confirmed phase */
    ${({ status }) =>
        status === 'saved-confirmed' &&
        css`
            background: ${theme.colors.background.darkGreen};
        `}

    /* Disabled (only from disabled prop, NOT from saving status) */
    ${({ disabled }) =>
        disabled &&
        css`
            cursor: not-allowed;
            opacity: 0.5;
        `}

    /* Checkmark: always in DOM, hidden by default, expands on in-flight + confirmed */
    .save-checkmark {
        display: inline-flex;
        align-items: center;
        width: 0;
        opacity: 0;
        overflow: hidden;
        font-size: 0.7rem;
        color: ${theme.colors.text.odfjellSection};
        flex-shrink: 0;
        transition: width 200ms ease, opacity 200ms ease, color 200ms ease;
    }

    ${({ status }) =>
        (status === 'saving' || status === 'saved-pending') &&
        css`
            .save-checkmark {
                width: 10px;
                opacity: 1;
                animation: ${fadeIn} 200ms ease-out;
            }
        `}

    ${({ status }) =>
        status === 'saved-confirmed' &&
        css`
            .save-checkmark {
                width: 10px;
                opacity: 1;
                color: ${theme.colors.icon.approved};
            }
        `}

    .save-label {
        white-space: nowrap;
    }

    @media (prefers-reduced-motion: reduce) {
        .save-checkmark {
            animation: none;
        }
    }
`;
